import { Carregando, useNotificacoes } from "components";
import React, { useCallback, useEffect, useState } from "react";
import { Route, RouteProps, useHistory } from "react-router-dom";
import { useGetLocalizacaoUsuario } from "utils";
import { isValidUUIDV4 } from "utils/is-valid-id";
import { LocalizacaoErroPage } from "./components/localizacao-fora-loja-page";
import { DistanciaModel } from "api/master/models/distancia/distancia-model";
import { postDistancia } from "api/master/mutations/distancia/enviar-distancia";
import { useMasterApi } from "../hooks";
import { validarTelefone } from "utils/validar-telefone";
import { obterMesa } from "api/pedidos/queries";
import { CardapioStorageKeys, useCardapioStorage } from "storage/cardapio-storage";
import { getIDFromUrl } from "utils/get-id-url";
import { useObterToken } from "services/store/obter-token";
import { usePessoaUsuarioStore } from "services/store/pessoa-usuario";

export interface LocalizacaoUsuarioProps extends RouteProps {
    empresaId: string;
    mesaId: string;
}

enum EnumErroLocalizacao {
    MesaInvalida,
    ForaLoja,
    LocalizacaoRecusada
}

export const LocalizacaoUsuario: React.FC<LocalizacaoUsuarioProps> = ({ mesaId, empresaId, children, ...otherProps }: LocalizacaoUsuarioProps) => {
    const history = useHistory();

    // STATES
    const [permitirPagina, setPermitirPagina] = useState<boolean>(false)
    const [erroLocalizacao, setErroLocalizacao] = useState<EnumErroLocalizacao>()

    // REQUISIÇÕES
    const { obterLocalizacao } = useGetLocalizacaoUsuario()
    const { showErrorMessage } = useNotificacoes()
    const { dados } = useMasterApi(empresaId)
    const { setRegistro } = useCardapioStorage()

    const empresa = dados.data?.empresa
    const configCardapio = dados.data?.configuracoes

    const handleClose = () => {
        history.push(`/${empresaId}`);
    };

    const pegarMesa = useCallback(async () => {
        const mesaId = getIDFromUrl('mesaId');
        if (isValidUUIDV4(mesaId)) {
            const ret = await obterMesa.execute(empresaId, mesaId)
            if (!ret) return false
            setRegistro(CardapioStorageKeys.MesaAtual, ret, false)
            return ret
        }
    }, [empresaId, setRegistro]);

    const postDistanciaWrapper = useCallback(async (model: DistanciaModel) => {
        const res = await postDistancia.execute(model, empresaId);

        return res.dentro;
    }, [empresaId])

    const localizacaoUsuario = useCallback(async () => {
        if (mesaId !== undefined && isValidUUIDV4(mesaId)) {
            let erro = false
            let localizacao: DistanciaModel | null = null
            try {
                const res = await pegarMesa()
                if (!res) {
                    setErroLocalizacao(EnumErroLocalizacao.MesaInvalida)
                    return;
                }
                useObterToken.getState().setToken(undefined)
                usePessoaUsuarioStore.getState().setPessoa(undefined)
            } catch (err: any) {
                setErroLocalizacao(EnumErroLocalizacao.MesaInvalida)
                return
            }
            if (configCardapio?.distanciaMaxima === -1) {
                setPermitirPagina(true);
                return;
            }
            try {
                localizacao = await obterLocalizacao();
            } catch (err: any) {
                erro = true
            }
            if (erro) {
                setErroLocalizacao(EnumErroLocalizacao.LocalizacaoRecusada)
                return
            }
            if (localizacao) {
                try {
                    const resposta = await postDistanciaWrapper({
                        latitude: localizacao.latitude,
                        longitude: localizacao.longitude,
                    });
                    if (!resposta) {
                        setPermitirPagina(false);
                        setErroLocalizacao(EnumErroLocalizacao.ForaLoja)
                        return
                    }
                    setPermitirPagina(true);
                } catch (err: any) {
                    showErrorMessage(`Erro ao buscar a sua localização atual. Detalhe: ${err.message}${validarTelefone(empresa?.fone ?? '')}`);
                }
            }
        }
    }, [configCardapio?.distanciaMaxima, empresa?.fone, mesaId, obterLocalizacao, pegarMesa, postDistanciaWrapper, showErrorMessage])

    const tentarNovamente = useCallback(() => {
        window.location.reload();
    }, [])

    useEffect(() => {
        if (configCardapio) {
            localizacaoUsuario();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configCardapio])

    const mensagemErro = useCallback(() => {
        switch (erroLocalizacao) {
            case EnumErroLocalizacao.MesaInvalida:
                return {
                    titulo: 'Mesa não encontrada',
                    mensagem: 'Ocorreu um problema e não encontramos sua mesa, não achamos ou ela não existe, tente novamente ou tente outra mesa.'
                }
            case EnumErroLocalizacao.LocalizacaoRecusada:
                return {
                    titulo: 'Precisamos da sua localização para continuar',
                    mensagem: 'Para acessar o catálogo, precisamos verificar onde você está. Ative a permissão de localização no seu navegador e tente novamente!'
                }
            case EnumErroLocalizacao.ForaLoja:
                return {
                    titulo: 'Você está fora da área permitida',
                    mensagem: 'Para continuar com o acesso, você precisa estar dentro da área da nossa loja. Verifique sua localização, aproxime-se para seguir em frente e tente novamente!'
                }
            default:
                return {
                    titulo: 'Não foi possível acessar sua localização',
                    mensagem: 'Tivemos um problema ao tentar obter sua localização. Verifique as configurações do seu dispositivo e tente novamente.'
                }
        }
    }, [erroLocalizacao])

    if (erroLocalizacao !== undefined) {
        return (
            <LocalizacaoErroPage
                cardapioPrincipal={handleClose}
                tentarNovamente={tentarNovamente}
                titulo={mensagemErro().titulo}
                mensagem={mensagemErro().mensagem}
            />
        )
    }

    if (!permitirPagina) {
        return (
            <Carregando
                titulo={'Carregando Dados'}
                mensagem={'Aguarde enquanto carregamos a página.'}
                modelo="fixed"
                carregando
            />
        );
    }

    return (
        <Route {...otherProps}>
            {children}
        </Route>
    )
}